import React from "react";

import Button from "@common/components/Button/Button";
import { HelperText } from "@common/components/HelperText/HelperText";
import { ModalProps } from "@common/components/Modal/Modal";
import SelectDropdown, { Option } from "@common/components/SelectDropdown/SelectDropdown";
import { Workspace } from "@common/models";
import {
  forwardingOptions,
  getIntegrationTypeName,
  IntegrationLayoutEntry,
  IntegrationType,
} from "@common/models/integrations";
import { Github } from "@common/models/integrations/Github.model";
import { IntegrationLayoutSettings } from "@main/pages/Administration/components/ModalBody/UserFeedbackIntegrations/components/IntegrationLayoutSettings";
import { getIntegrationTypeLogo } from "@main/pages/Administration/util/getIntegrationTypeLogo";

import { Form } from "../ModalBody.styles";
import { GithubOption, useGithubIntegrationFormConsumer } from "./consumers/useGithubIntegrationFormConsumer";

interface GithubIntegrationModalProps {
  Modal: React.ComponentType<ModalProps>;
  modalProps: ModalProps;
  integration?: Github;
  selectedAppId: string;
  workspace: Workspace;
  onFinish: () => void;
}

export const GithubIntegrationModal = ({
  Modal,
  modalProps,
  integration,
  selectedAppId,
  onFinish,
}: GithubIntegrationModalProps) => {
  const {
    forwardingOption,
    projectOption,
    projectOptions,
    handleFormChange,
    handleFormSubmit,
    disabled,
    loading,
    loadOptions,
    integrationLayout,
    moveIntegrationLayoutEntryDown,
    moveIntegrationLayoutEntryUp,
    toggleIntegrationLayoutEntry,
    metadataKeys,
    metadataEnabledState,
    toggleMetadataKey,
    toggleAllMetadataKeys,
    setMetadataKeysEnabled,
    resetMetadataKeys,
    hasMetadataKeyChanges,
  } = useGithubIntegrationFormConsumer({ selectedAppId, onFinish, integration });

  const optionDidChange = (option: Option, name: string | undefined) => {
    name && handleFormChange(name, option);
  };

  const SaveButton = () => {
    const isButtonDisabled =
      checkIsButtonDisabled(disabled, projectOption, forwardingOption, integrationLayout, integration) &&
      !hasMetadataKeyChanges;

    return (
      <Button
        size="small"
        tooltip={{
          position: "top",
          text: "No changes have been made",
          disabled: !isButtonDisabled || disabled,
        }}
        disabled={isButtonDisabled || loading}
        loadingText={loading && "Saving..."}
        onClick={handleFormSubmit}
      >
        Save changes
      </Button>
    );
  };

  return (
    <Modal
      {...modalProps}
      key={IntegrationType.GITHUB}
      buttonElement={<SaveButton />}
      heading={`${getIntegrationTypeName(IntegrationType.GITHUB)} integration`}
      iconPath={getIntegrationTypeLogo(IntegrationType.GITHUB)}
    >
      <Form
        noValidate
        onSubmit={(event) => event.preventDefault()}
      >
        <SelectDropdown
          name="forwardingOption"
          value={forwardingOption}
          options={forwardingOptions}
          placeholder="Please choose"
          label="Forward tickets"
          onChange={optionDidChange}
          isSearchable={false}
          disabled={disabled || loading}
          hasSubtitles
        />
        <SelectDropdown
          name="projectOption"
          value={projectOption}
          options={projectOptions}
          placeholder="Please choose"
          label="Repository"
          onChange={optionDidChange}
          isSearchable={true}
          disabled={disabled}
          loadOptions={loadOptions}
        />
        <HelperText>
          The repository you need is not listed? Disconnect and then connect the integration again.
        </HelperText>

        <IntegrationLayoutSettings
          integrationLayout={integrationLayout}
          moveEntryUp={moveIntegrationLayoutEntryUp}
          moveEntryDown={moveIntegrationLayoutEntryDown}
          toggleEntry={toggleIntegrationLayoutEntry}
          metadataKeys={metadataKeys}
          metadataEnabledState={metadataEnabledState}
          toggleMetadataKey={toggleMetadataKey}
          toggleAllMetadataKeys={toggleAllMetadataKeys}
          setMetadataKeysEnabled={setMetadataKeysEnabled}
          resetMetadataKeys={resetMetadataKeys}
        />
      </Form>
    </Modal>
  );
};

const checkIsButtonDisabled = (
  disabled: boolean,
  projectOption: Option,
  forwardingOption: Option,
  integrationLayout: IntegrationLayoutEntry[],
  integration?: Github,
) => {
  if (disabled) return true;
  if (!projectOption || !forwardingOption) return true;
  if (!integration) return false;

  const parsedProjectOption = JSON.parse(projectOption.value) as GithubOption;

  // Check if repo or forwarding settings have changed
  const repoChanged = parsedProjectOption.repoId !== integration.repository_id;
  const forwardingChanged = !!forwardingOption.value !== integration.automatic_forward;

  if (repoChanged || forwardingChanged) {
    return false;
  }

  // Check if integration layout has changed
  return !(integration && hasIntegrationLayoutChanged(integrationLayout, integration.integration_layout));
};

const hasIntegrationLayoutChanged = (
  currentLayout: IntegrationLayoutEntry[],
  originalLayout?: IntegrationLayoutEntry[],
): boolean => {
  if (!originalLayout || originalLayout.length === 0) return currentLayout.length > 0;
  if (currentLayout.length !== originalLayout.length) return true;

  return currentLayout.some((entry, index) => {
    const originalEntry = originalLayout[index];
    return entry.key !== originalEntry.key || entry.enabled !== originalEntry.enabled;
  });
};
